import { useEffect, useState } from "react";
import { Logo } from "@/components/Logo";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Star, Loader2, CheckCircle2 } from "lucide-react";
import { api, apiErr } from "@/lib/api";

function Stars({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1.5" data-testid="review-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={`star-${n}`}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(n)}
          className="p-1 transition-transform hover:scale-110"
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
        >
          <Star className={`h-9 w-9 ${(hover || value) >= n ? "fill-amber-400 text-amber-400" : "text-slate-600"}`} />
        </button>
      ))}
    </div>
  );
}

export default function PublicReview() {
  const { workspaceId } = useParams();
  const [params] = useSearchParams();
  const jobId = params.get("job");
  const [company, setCompany] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [rating, setRating] = useState(0);
  const [f, setF] = useState({ name: "", company: "", comment: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [ratingErr, setRatingErr] = useState(false);

  useEffect(() => {
    api.get(`/public/reviews/ws/${workspaceId}`)
      .then(({ data }) => setCompany(data.company))
      .catch(() => setNotFound(true));
  }, [workspaceId]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { setRatingErr(true); toast.error("Please pick a star rating"); return; }
    setBusy(true);
    try {
      await api.post(`/public/reviews/ws/${workspaceId}`, {
        rating, comment: f.comment, name: f.name, company: f.company,
        job_id: jobId || undefined, website: f.website,
      });
      setDone(true);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail) || "Could not submit your review"); }
    setBusy(false);
  };

  if (notFound) return <div className="grid min-h-screen place-items-center bg-[#0B0F17] text-slate-400" data-testid="review-notfound">This review link is not available.</div>;

  const inp = "w-full rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-amber-500 focus:outline-none";
  const lbl = "mb-1 block font-mono-tac text-[10px] uppercase tracking-widest text-slate-500";

  return (
    <div className="min-h-screen bg-[#0B0F17] px-4 py-12 text-slate-200" data-testid="public-review-page">
      <div className="mx-auto max-w-md">
        <div className="mb-6 flex items-center gap-2.5"><Logo /></div>
        {done ? (
          <div className="rounded-2xl border border-emerald-600/40 bg-emerald-500/10 p-8 text-center" data-testid="review-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
            <h1 className="mt-4 font-display text-2xl font-bold text-white">Thank you!</h1>
            <p className="mt-2 text-sm text-slate-300">Your review of {company} has been submitted and will appear once approved. We appreciate you taking the time.</p>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-800 bg-[#0d1420] p-6">
            <p className="font-mono-tac text-xs uppercase tracking-widest text-cyan-400">Leave a review</p>
            <h1 className="mt-2 font-display text-2xl font-bold text-white">How did {company || "we"} do?</h1>
            <p className="mt-2 text-sm text-slate-400">Your feedback helps other customers and helps us keep improving.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
                <label>Website<input data-testid="review-website" type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} /></label>
              </div>
              <div>
                <label className={lbl}>Your rating</label>
                <Stars value={rating} onChange={(n) => { setRating(n); setRatingErr(false); }} />
                {ratingErr && <p className="mt-1 text-xs text-red-400" data-testid="rating-error">Please pick a star rating before submitting.</p>}
              </div>
              <div><label className={lbl}>Your name</label><input data-testid="review-name" value={f.name} onChange={set("name")} placeholder="Your name" className={inp} /></div>
              <div><label className={lbl}>Company (optional)</label><input data-testid="review-company" value={f.company} onChange={set("company")} placeholder="Company (optional)" className={inp} /></div>
              <div><label className={lbl}>Comment</label><textarea data-testid="review-comment" rows={4} value={f.comment} onChange={set("comment")} placeholder="Tell us about your experience" className={inp} /></div>
              <button data-testid="review-submit" type="submit" disabled={busy} className="btn-amber inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4" />} {busy ? "Submitting..." : "Submit review"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
